/**
 * useSessionEvents — the model-stream watch connection (care-app/ADR-0023).
 *
 * Opens a persistent SSE connection to GET /api/v1/events carrying events on
 * the MODELS this page watches — a stay's care plan, a ward's roster. Stream
 * events are routed by model rather than by user, de-duplicated by `seq`
 * rather than by event id, and are never in an outbox, so this connection has
 * no replay cursor: catch-up is the model's own `?after=<seq>` read, driven by
 * the store. The watch set comes from `useModelStreamsStore`: it rides in the
 * query string on connect and changes on an open connection through POST
 * /events/subscriptions, addressed by the connection id the server sends in
 * its first frame.
 *
 * The conversation events this connection also used to carry (turn.* deltas,
 * session_shared/updated/deleted, ui_action_*, fabric directives) are gone
 * with the session cut-over: their publishers died with the session module,
 * and the conversation's own stream is per-session on tricorder-session
 * (useSessionEventStream). What remains here is exactly the watch substrate.
 *
 * Design: eventual consistency. SSE is an accelerator, not a dependency.
 * - Connection succeeds → real-time updates.
 * - Transient failure to establish (first-attempt 5xx / empty body / thrown
 *   network|timeout error) → reconnect. A cold start is the design, not an
 *   outage, so we keep trying rather than killing live updates for the whole
 *   session.
 * - Definitive failure to establish (first-attempt 4xx: 404 endpoint absent on
 *   an out-of-date client, or 401/403 auth) → disabled. No retry — the banner
 *   offers a reload.
 * - Established connection drops (Cloud Run ~300s cycling) → reconnect.
 * - Page hidden → closed; page visible again → reconnect. An open stream bills
 *   a whole vCPU on Cloud Run, so a hidden tab holds none.
 */

import { useEffect } from "react";
import { useAuthStore } from "@/stores/auth";
import {
  classifyResetFrame,
  classifyStreamEvent,
  classifyWatchSetFrame,
} from "@/api/streamEvents";
import { useModelStreamsStore } from "@/stores/modelStreams";
import { useSSEConnectionStore } from "@/stores/sseConnection";
import { getApiBaseUrl } from "@/api/baseUrl";
import { createLogger } from "@/utils/logger";

const logger = createLogger("SessionEvents");

const BASE_URL = getApiBaseUrl();
const EVENTS_PATH = "/events";
const SUBSCRIPTIONS_PATH = "/events/subscriptions";
const RECONNECT_DELAY_MS = 1000;

export function useSessionEvents(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const authReady = useAuthStore((s) => s.authReady);
  const accessToken = useAuthStore((s) => s.accessToken);
  const setConnectionStatus = useSSEConnectionStore((s) => s.setStatus);

  useEffect(() => {
    if (!isAuthenticated || !authReady || !accessToken) {
      return;
    }

    let abortController = new AbortController();
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let unmounted = false;
    // The connection is held only while the page is visible: an open stream
    // bills a whole vCPU on Cloud Run for as long as it is open.
    const closed = (): boolean =>
      unmounted || document.visibilityState === "hidden";
    let wasConnected = false;
    setConnectionStatus("connecting");

    /**
     * Single error handler for any failure surfaced by `connect()` — fetch
     * rejection, stream read error, anything throwable. Used by every
     * `void connect()` invocation site so that an exception thrown by a
     * RECONNECT attempt (not just the initial connect) is observed and
     * routed to the same wasConnected → reconnect / disabled decision tree.
     * Without this, a throw during a reconnect would leave status stuck
     * at 'connecting' with no further retry.
     */
    const handleConnectError = (err: unknown): void => {
      if (closed()) return;
      if (err instanceof DOMException && err.name === "AbortError") return;

      const message = err instanceof Error ? err.message : "Unknown error";

      if (wasConnected) {
        wasConnected = false;
        scheduleReconnect(`dropped (${message})`);
      } else {
        // A thrown error (network blip, DNS, connection refused, cold-start
        // timeout) is always transient — a genuine "endpoint absent" returns a
        // 404 *status* (handled in connect()), it doesn't throw. Keep retrying
        // so a cold start doesn't permanently kill live updates for the session.
        scheduleReconnect(`connect failed (${message})`);
      }
    };

    /**
     * Schedule a reconnect after a transient failure (stream end, dropped
     * connection, non-ok response on a previously-working stream). Used by
     * three sites: normal stream-end, handleConnectError, and the non-ok
     * branch inside connect(). Centralised so the jitter/delay/status
     * semantics stay identical — drift between these three sites is what
     * caused the "permanent disable on reconnect 5xx" bug.
     */
    const scheduleReconnect = (reason: string): void => {
      if (closed()) return;
      logger.debug("SSE reconnecting", { reason });
      setConnectionStatus("disconnected");
      // The watch set belongs to the connection, and the connection id the
      // server issued is gone with it. The reconnect states the full set again
      // in its query string.
      useModelStreamsStore.getState().connectionLost();
      reconnectTimer = setTimeout(
        () => {
          if (closed()) return;
          setConnectionStatus("connecting");
          // .catch is required: this connect() runs detached from the original
          // promise chain, so without a handler any thrown error would be
          // silently swallowed and the status would freeze at 'connecting'.
          void connect().catch(handleConnectError);
        },
        RECONNECT_DELAY_MS + Math.random() * 500,
      );
    };

    /**
     * Send the difference between the models this page wants to watch and the
     * ones the server says this connection watches.
     *
     * There is no retry, no timeout and no backoff. If this POST fails the
     * confirmed set does not change, so the next change to the wanted set
     * sends the difference again — and the connection is torn down and rebuilt
     * every few minutes anyway (Cloud Run's request deadline), which states
     * the whole set afresh in the query string. Those are the platform's own
     * recovery paths, not ones invented here.
     *
     * Sending the same add twice is harmless: the server applies a set
     * membership change, so a replay leaves the connection watching exactly
     * what it watched after the first application.
     */
    // postWatchChange sends one subscription change and returns nothing: the
    // answer is acted on here, and the caller has no decision left to make.
    const postWatchChange = (
      connectionId: string,
      body: { add?: string[]; remove?: string[] },
    ): void => {
      void fetch(`${BASE_URL}${SUBSCRIPTIONS_PATH}`, {
        method: "POST",
        credentials: "omit",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ connection_id: connectionId, ...body }),
        signal: abortController.signal,
      })
        .then((response) => {
          if (response.ok) return;
          // 403 means access to this subject ended; 400 means a token this
          // client built is not one the server can read. Both are the server's
          // answer about the token in THIS request, and neither is retried.
          logger.warn("Watch subscription change refused", {
            status: response.status,
            ...body,
          });
          if (
            (response.status === 403 || response.status === 400) &&
            body.add !== undefined
          ) {
            useModelStreamsStore.getState().refuseWatches(body.add);
          }
        })
        .catch((err: unknown) => {
          if (err instanceof DOMException && err.name === "AbortError") return;
          logger.warn("Watch subscription change failed to send", {
            error: err instanceof Error ? err.message : "unknown",
          });
        });
    };

    const syncWatches = (): void => {
      const { connectionId, wanted, confirmed } =
        useModelStreamsStore.getState();
      if (connectionId === null) return;

      const add = wanted.filter((token) => !confirmed.includes(token));
      const remove = confirmed.filter((token) => !wanted.includes(token));
      if (add.length === 0 && remove.length === 0) return;

      // ONE REQUEST PER ADDED TOKEN. The endpoint authorises every token in a
      // request and refuses the whole request on the first one it will not
      // allow, exactly as `GET /events` does. Batching the adds would therefore
      // give back the same answer that cannot name a token, and the client
      // would be guessing again — which is the defect this whole path exists to
      // remove. One token per request makes the refusal name the model it is
      // about, with no bisection, no retry and no counter. A page watches one
      // or two models, so this is one or two requests, not a fan-out.
      for (const token of add) {
        postWatchChange(connectionId, { add: [token] });
      }
      // Removes go together. A removal needs no authority over the subject —
      // the caller is giving something up — so nothing here can be refused for
      // one token and allowed for another.
      if (remove.length > 0) {
        postWatchChange(connectionId, { remove });
      }
    };

    const parseAndDispatch = (eventType: string, dataLine: string): void => {
      if (eventType === "keepalive") return;
      if (dataLine === "") return;

      // The three control frames of the model-watch protocol. `connected` is
      // always the server's first frame and carries the connection id that
      // makes POST /events/subscriptions addressable at all — this client is a
      // fetch plus a ReadableStream rather than an EventSource (EventSource
      // cannot send an Authorization header), so there is no URL it already
      // knows to name its own connection by.
      if (eventType === "connected" || eventType === "watching") {
        try {
          const result = classifyWatchSetFrame(JSON.parse(dataLine));
          if (!result.ok) {
            logger.warn("Dropping malformed watch-set frame", { eventType });
            return;
          }
          useModelStreamsStore
            .getState()
            .applyWatchSet(result.frame.connectionId, result.frame.watching);
        } catch (err) {
          logger.warn("Failed to parse watch-set SSE data", {
            eventType,
            error: err instanceof Error ? err.message : "unknown",
          });
        }
        return;
      }

      // The server stopped keeping this connection up to date on a model and
      // tore that watch down. The board re-reads the fold; the store drops the
      // model from the confirmed set, which is what makes syncWatches re-watch
      // it. No backoff here — the reconnect delay above is the only clock this
      // client has, and a reset is not a reconnect.
      if (eventType === "reset") {
        try {
          const result = classifyResetFrame(JSON.parse(dataLine));
          if (!result.ok) {
            logger.warn("Dropping malformed reset frame", { eventType });
            return;
          }
          useModelStreamsStore.getState().resetModel(result.model);
        } catch (err) {
          logger.warn("Failed to parse reset SSE data", {
            error: err instanceof Error ? err.message : "unknown",
          });
        }
        return;
      }

      // A stream event is recognised by its SHAPE. Its `event:` line carries
      // the domain event type (`roster.published`), which this
      // client holds no list of and must not need one of: a new event type
      // on a stream a board watches is data, not a client release.
      try {
        const parsed: unknown = JSON.parse(dataLine);
        const stream = classifyStreamEvent(eventType, parsed);
        if (stream.ok) {
          useModelStreamsStore.getState().deliver(stream.event);
          return;
        }
        logger.warn("Dropping SSE event of an unrecognised shape", { eventType });
      } catch (err) {
        logger.warn("Failed to parse SSE data", {
          eventType,
          error: err instanceof Error ? err.message : "unknown",
        });
      }
    };

    const connect = async (): Promise<void> => {
      if (closed()) return;

      // State the whole watch set in the query string. A reconnect is the one
      // moment the client knows more than the server does about what it wants
      // to watch, so it says all of it rather than opening bare and following
      // up with a POST that would race the first events.
      // Everything wanted, minus anything a previous connect refused as a set.
      // A connect-403 does not say which token it objected to, so the answer is
      // to try again without them and let the per-token endpoint sort them out
      // once the connection is up. See `deferredFromConnect` in the store.
      const watchTokens = useModelStreamsStore.getState().connectQueryTokens();
      const params = new URLSearchParams();
      for (const token of watchTokens) {
        params.append("watch", token);
      }
      const query = params.toString();
      const url = `${BASE_URL}${EVENTS_PATH}${query === "" ? "" : `?${query}`}`;
      const headers: Record<string, string> = {
        Accept: "text/event-stream",
        Authorization: `Bearer ${accessToken}`,
        "Cache-Control": "no-cache",
      };

      const response = await fetch(url, {
        method: "GET",
        credentials: "omit",
        headers,
        signal: abortController.signal,
      });

      if (!response.ok || !response.body) {
        // A 403 on a request that carried watch tokens is the server refusing
        // one of them, and the refusal takes the whole request with it — so
        // this is NOT an auth failure of the connection itself and must not
        // disable live updates.
        //
        // DEFER the set rather than refusing it. The answer names no token, so
        // refusing all of them would punish every model on the page for one
        // revoked stay, and permanently: they would leave `wanted`, syncWatches
        // would have nothing to differ on, and each board would re-read its
        // fold once and then never update again. Deferring keeps them wanted
        // and only holds them out of the NEXT query string, which is enough to
        // get the connection back. The `connected` frame clears the deferral
        // and syncWatches then offers each token on its own, where a 403 names
        // the one model it is about.
        if (response.status === 403 && watchTokens.length > 0) {
          useModelStreamsStore.getState().deferWatchesFromConnect(watchTokens);
          scheduleReconnect("watch set refused by connect");
          return;
        }

        // Reconnect rather than kill SSE when the failure is transient: a blip
        // on a previously-working stream (Cloud Run rolling deploy, pod
        // restart), a first-attempt 5xx (cold instance not serving yet — a
        // cold start is the design, not a capability gap), or an empty body.
        // Only a first-attempt 4xx is a definitive dead-end where retrying
        // can't help — 404 (endpoint absent on an out-of-date client) or
        // 401/403 (auth) — so disable for this session and let the banner
        // offer reload.
        const transient = response.ok || response.status >= 500;
        if (wasConnected || transient) {
          scheduleReconnect(
            `non-ok response (status=${String(response.status)})`,
          );
        } else {
          logger.debug("SSE endpoint unavailable, disabled for this session", {
            status: response.status,
          });
          setConnectionStatus("disabled");
        }
        return;
      }

      wasConnected = true;
      setConnectionStatus("connected");
      logger.debug("SSE connected");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let eventType = "";

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          // NOTE: The SSE spec allows multiple `data:` lines per event block
          // (concatenated with \n). This parser dispatches on the first `data:`
          // line it sees. Our server always sends compact single-line JSON, so
          // multi-line accumulation is unnecessary. Do not "fix" this without
          // also updating the server to emit multi-line data.
          for (const line of lines) {
            if (line.startsWith("event: ")) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith("data: ")) {
              parseAndDispatch(eventType, line.slice(6));
              eventType = "";
            } else if (line === "") {
              eventType = "";
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      // Stream ended normally (Cloud Run ~300s timeout). Reconnect; stream
      // catch-up is the model's own ?after=<seq> read, not a cursor here.
      scheduleReconnect("stream ended");
    };

    void connect().catch(handleConnectError);

    // Hiding the page closes the connection the way a drop does, and showing
    // it again connects the way a reconnect does: the full watch set in the
    // query string. Visibility is the only trigger; there is no idle timer.
    const onVisibilityChange = (): void => {
      if (document.visibilityState === "hidden") {
        abortController.abort();
        if (reconnectTimer !== null) {
          clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }
        wasConnected = false;
        setConnectionStatus("disconnected");
        useModelStreamsStore.getState().connectionLost();
        return;
      }
      abortController = new AbortController();
      setConnectionStatus("connecting");
      void connect().catch(handleConnectError);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    // One rule for every reason the two sets can differ: a board mounting or
    // unmounting changes `wanted`; the `connected` and `watching` frames set
    // `confirmed`; a `reset` drops one model out of `confirmed`, which is what
    // re-watches it. Each of those is the same instruction — send the
    // difference — so they share one subscription rather than each remembering
    // to call it.
    const unsubscribeWatches = useModelStreamsStore.subscribe(
      (state, previous) => {
        if (
          state.wanted !== previous.wanted ||
          state.confirmed !== previous.confirmed
        ) {
          syncWatches();
        }
      },
    );

    return () => {
      unmounted = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      unsubscribeWatches();
      abortController.abort();
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
      }
      useModelStreamsStore.getState().connectionLost();
    };
    // accessToken is intentionally in the dep array: token rotation tears down
    // and rebuilds the SSE connection — do not "fix" by removing.
  }, [isAuthenticated, authReady, accessToken, setConnectionStatus]);
}
